// screens.js — faithful full-product screens #1-2 for the hero showcase (ported
// from website2-screens.jsx): Dashboard and Calendar, composed 1:1 from the
// design system (nm-* classes, tokens) with the real seed data.
import React from 'react'
import { NIc } from './icons'
import { useN2Phases } from './shell'

/* count-up for the revenue hero */
export function N2Count({ to, active, dur = 1100, prefix = '$' }) {
	const reduce = React.useRef(window.matchMedia('(prefers-reduced-motion: reduce)').matches).current
	const [v, setV] = React.useState(reduce ? to : 0)
	React.useEffect(() => {
		if (reduce) return
		if (!active) { setV(0); return }
		let raf, t0
		const tick = (t) => {
			if (!t0) t0 = t
			const p = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - p, 3)
			setV(Math.round(to * e))
			if (p < 1) raf = requestAnimationFrame(tick)
		}
		raf = requestAnimationFrame(tick)
		return () => cancelAnimationFrame(raf)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [active])
	return <span>{prefix}{v.toLocaleString('en-US')}</span>
}

export function N2Spark({ pts, w = 96, h = 26, tone = 'var(--primary)' }) {
	const min = Math.min(...pts), max = Math.max(...pts)
	const xy = pts.map((p, i) => (i / (pts.length - 1) * w).toFixed(1) + ',' + (h - 3 - (p - min) / (max - min || 1) * (h - 6)).toFixed(1))
	return (
		<svg className="sd-spark" width={w} height={h} viewBox={'0 0 ' + w + ' ' + h} aria-hidden="true">
			<polyline points={xy.join(' ')} fill="none" stroke={tone} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.85"></polyline>
			<circle cx={w} cy={xy[xy.length - 1].split(',')[1]} r="2.6" fill={tone}></circle>
		</svg>
	)
}

/* staff headshots — shared by the Calendar columns/chips and the Dashboard floor rows (stock placeholders; swap for the real team) */
const N2_STAFF_PHOTO = {
	AS: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?q=80&w=96&h=96&auto=format&fit=crop&crop=faces',
	ML: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=96&h=96&auto=format&fit=crop&crop=faces',
	PP: 'https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?q=80&w=96&h=96&auto=format&fit=crop&crop=faces',
	JK: 'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?q=80&w=96&h=96&auto=format&fit=crop&crop=faces'
}
export const N2StaffPhoto = ({ ini, name }) => <img className="s-staffpic" src={N2_STAFF_PHOTO[ini]} alt={name} loading="lazy" draggable="false" />

/* ── 1 · Dashboard ──────────────────────────────────────────────── */
const SD_KPIS = [
	{ label: 'Appointments', value: '312', delta: '+8.2%', up: true, note: '253 completed · 59 still to come', spark: [18, 22, 19, 24, 21, 26, 23, 27, 25, 29, 26, 30, 28, 31] },
	{ label: 'Average ticket', value: '$73.28', delta: '+3.4%', up: true, note: 'Add-ons on 61% of tickets', spark: [66, 68, 67, 69, 68, 70, 69, 71, 70, 72, 71, 72, 73, 73.3] },
	{ label: 'Rebook rate', value: '62.4%', delta: '−1.8 pts', up: false, note: '11 clients left without a next visit', spark: [64, 65, 66, 65, 67, 66, 65, 64, 65, 63, 64, 63, 63, 62.4] }
]
const SD_FLOOR = [
	{ ini: 'AS', ac: '#7C6BD9', soft: '#EFECFB', deep: '#5245AC', who: 'Ashley Smith', pet: 'Milo — Cavapoo', svc: 'Full groom — S · until 12:00', pill: ['In the tub', 'primary'] },
	{ ini: 'ML', ac: '#3E9BD0', soft: '#E9F4FB', deep: '#20678F', who: 'Marcus Lee', pet: 'Biscuit — Bernese Mountain', svc: 'Full groom — L · until 11:45', pill: ['In the tub', 'primary'] },
	{ ini: 'JK', ac: '#D9912F', soft: '#FBF2E1', deep: '#8F6217', who: 'Jordan Kim', pet: 'Bronny — Labrador', svc: 'Bath & brush · 11:00', pill: ['Checked in', 'info'] },
	{ ini: 'PP', ac: '#46A06E', soft: '#E9F5EE', deep: '#2A6E4C', who: 'Priya Patel', pet: 'Kitty — Maine Coon', svc: 'Cat bath & tidy · 11:00', pill: ['Up next', 'neutral'] }
]
export function N2ScrDashboard({ on }) {
	return (
		<div className="s-page sd">
			<div className="nm-pagehead">
				<h1>Dashboard</h1>
				<div className="s-row" style={{ gap: 10 }}>
					<span className="nm-btn-ghost sm">Export</span>
					<span className="nm-btn-ghost sm">Customize</span>
				</div>
			</div>
			<div className="sd-grid">
				<div className="sd-col">
					<div className="nm-card pad sd-hero">
						<div className="s-row sd-liverow">
							<span className="sd-live"><i></i>LIVE</span>
							<strong className="sd-today">Today</strong>
							<span className="sd-date">Thursday, July 23, 2026 · 10:47 AM</span>
							<span className="nm-link" style={{ marginLeft: 'auto' }}>Open calendar <NIc name="chevr" size={11} sw={2} /></span>
						</div>
						<span className="nm-overline">On the books today</span>
						<div className="s-row" style={{ gap: 14, alignItems: 'center' }}>
							<span className="sd-big"><N2Count to={1295} active={on} /></span>
							<span className="sd-vs">$438 above a typical Thursday</span>
						</div>
						<div className={'sd-bar' + (on ? ' go' : '')} aria-hidden="true">
							<i style={{ '--w': '6.6%', background: 'var(--ok-ic)' }}></i>
							<i style={{ '--w': '7.3%', background: 'var(--warn-ic)', '--d': '.12s' }}></i>
							<i style={{ '--w': '16.2%', background: 'var(--primary)', '--d': '.24s' }}></i>
						</div>
						<div className="sd-quad">
							<div className="sd-q"><i style={{ background: 'var(--ok-ic)' }}></i><div><strong>$85</strong><span>Collected</span><em>3 appointments</em></div></div>
							<div className="sd-q"><i style={{ background: 'var(--warn-ic)' }}></i><div><strong>$95</strong><span>Awaiting payment</span><em>1 appointment</em></div></div>
							<div className="sd-q"><i style={{ background: 'var(--primary)' }}></i><div><strong>$210</strong><span>In progress</span><em>2 appointments</em></div></div>
							<div className="sd-q"><i style={{ background: '#CDC3EF' }}></i><div><strong>$905</strong><span>Still to come</span><em>11 appointments</em></div></div>
						</div>
					</div>
					<div className="sd-kpis">
						{SD_KPIS.map((k, i) => (
							<div className="nm-card pad sd-kpi" key={k.label} style={{ '--d': (i * 0.07) + 's' }}>
								<span className="sd-kpi-l">{k.label}</span>
								<div className="s-row" style={{ gap: 8, alignItems: 'baseline' }}>
									<strong className="sd-kpi-v">{k.value}</strong>
									<span className={'sd-delta' + (k.up ? '' : ' down')}>{k.delta}</span>
								</div>
								<N2Spark pts={k.spark} tone={k.up ? 'var(--primary)' : 'var(--warn-ic)'} />
								<span className="sd-kpi-n">{k.note}</span>
							</div>
						))}
					</div>
				</div>
				<div className="nm-card sd-floor">
					<div className="nm-card-head"><strong>The floor — right now</strong><span>10:47 AM</span></div>
					<div className="sd-rows">
						{SD_FLOOR.map((r, i) => (
							<div className="sd-frow" key={r.ini} style={{ '--d': (0.15 + i * 0.09) + 's' }}>
								<span className="nm-avatar" style={{ background: r.soft, color: r.deep }}><N2StaffPhoto ini={r.ini} name={r.who} /></span>
								<div className="sd-ftx">
									<span className="sd-fwho">{r.who}</span>
									<strong>{r.pet}</strong>
									<span className="sd-fsvc">{r.svc}</span>
								</div>
								<span className={'nm-pill ' + (r.pill[1] === 'primary' ? 'booked' : r.pill[1] === 'info' ? 'sd-pill-info' : 'canceled')}><i className="nm-pill-dot"></i>{r.pill[0]}</span>
							</div>
						))}
					</div>
					<div className="sd-collect">
						<div className="sd-ftx" style={{ flex: 1 }}>
							<strong>Luna — Poodle is done</strong>
							<span className="sd-fsvc">Full groom — M · Alex Morgan</span>
						</div>
						<span className="nm-btn sm">Collect $95</span>
					</div>
				</div>
			</div>
		</div>
	)
}

/* ── 2 · Calendar — day view ────────────────────────────────────── */
const SC_H = 84, SC_START = 540 // 9:00 AM, 84px/hour
const scY = (m) => (m - SC_START) / 60 * SC_H
const SC_CATS = { groom: ['#EFECFB', '#7C6BD9', '#5245AC'], bath: ['#E9F4FB', '#3E9BD0', '#20678F'], puppy: ['#E9F5EE', '#46A06E', '#2A6E4C'], quick: ['#FBF2E1', '#D9912F', '#8F6217'] }
const SC_COLS = [
	{ ini: 'AS', name: 'Ashley Smith', role: 'Senior groomer', ac: '#7C6BD9', soft: '#EFECFB', deep: '#5245AC', appts: [
		{ pet: 'Luna', breed: 'Maltese', svc: 'Full Groom — S', cat: 'groom', at: 540, mins: 75, done: true },
		{ pet: 'Biscuit', breed: 'Goldendoodle', svc: 'Full Groom — M', cat: 'groom', at: 645, mins: 75, now: true },
		{ pet: 'Louie', breed: 'Cavalier', svc: 'Bath & Brush', cat: 'bath', at: 780, mins: 45 },
		{ pet: 'Pepper', breed: 'Shih Tzu', svc: 'Full Groom — S', cat: 'groom', at: 855, mins: 60 }] },
	{ ini: 'ML', name: 'Marcus Lee', role: 'Groomer', ac: '#3E9BD0', soft: '#E9F4FB', deep: '#20678F', appts: [
		{ pet: 'Cooper', breed: 'Labrador', svc: 'Full Groom — L', cat: 'groom', at: 600, mins: 75 },
		{ pet: 'Waffles', breed: 'Corgi', svc: 'Bath & Brush', cat: 'bath', at: 705, mins: 45, drop: true },
		{ pet: 'Otis', breed: 'Dachshund', svc: 'Bath & Brush', cat: 'bath', at: 810, mins: 40 }] },
	{ ini: 'PP', name: 'Priya Patel', role: 'Groomer', ac: '#46A06E', soft: '#E9F5EE', deep: '#2A6E4C', offBefore: 660, appts: [
		{ pet: 'Peaches', breed: 'Cavapoo', svc: 'Puppy Intro', cat: 'puppy', at: 660, mins: 30 },
		{ pet: 'Nori', breed: 'French Bulldog', svc: 'Bath & Brush', cat: 'bath', at: 735, mins: 45 },
		{ pet: 'Milo', breed: 'Corgi', svc: 'Nail Trim', cat: 'quick', at: 840, mins: 15 }] },
	{ ini: 'JK', name: 'Jordan Kim', role: 'Bather', ac: '#D9912F', soft: '#FBF2E1', deep: '#8F6217', offAfter: 840, appts: [
		{ pet: 'Benny', breed: 'Beagle', svc: 'Nail Trim', cat: 'quick', at: 555, mins: 15, done: true },
		{ pet: 'Missile', breed: 'Chihuahua', svc: 'Nail Trim', cat: 'quick', at: 600, mins: 15, done: true },
		{ pet: 'Bronny', breed: 'Labrador', svc: 'Bath & Brush', cat: 'bath', at: 690, mins: 40 },
		{ pet: 'Wolverine', breed: 'Husky', svc: 'De-shedding', cat: 'quick', at: 780, mins: 15 }] }
]
const scT = (m) => { const h = Math.floor(m / 60) % 12 || 12, mm = m % 60; return h + (mm ? ':' + String(mm).padStart(2, '0') : '') + (m >= 720 ? ' PM' : ' AM') }

export function N2ScrCalendar({ on }) {
	const phase = useN2Phases(on, [[900, 'drop']])
	return (
		<div className="s-page sc">
			<div className="sc-toolbar">
				<span className="nm-iconbtn"><NIc name="chevl" size={14} sw={1.9} /></span>
				<span className="nm-iconbtn"><NIc name="chevr" size={14} sw={1.9} /></span>
				<span className="nm-btn-ghost sm">Today</span>
				<strong className="sc-title">Thursday, July 9, 2026</strong>
				<div className="nm-tabs sc-views">
					<span className="nm-tab on">Day</span><span className="nm-tab">Week</span><span className="nm-tab">Month</span>
				</div>
				<div className="sc-gchips">
					<span className="nm-fchip on" style={{ height: 30 }}>All</span>
					{SC_COLS.map((g) => (
						<span className="sc-gchip" key={g.ini} title={g.name} style={{ '--ac': g.ac, '--soft': g.soft, '--deep': g.deep }}><N2StaffPhoto ini={g.ini} name={g.name} /></span>
					))}
				</div>
			</div>
			<div className="sc-grid nm-card">
				<div className="sc-gutter">
					{[540, 600, 660, 720, 780, 840].map((m) => <span key={m} style={{ top: scY(m) + 40 }}>{scT(m)}</span>)}
				</div>
				<div className="sc-cols">
					{SC_COLS.map((g) => (
						<div className="sc-col" key={g.ini}>
							<div className="sc-colhead">
								<span className="sc-gava" style={{ background: g.soft, color: g.deep }}><N2StaffPhoto ini={g.ini} name={g.name} /></span>
								<div><strong>{g.name}</strong><span>{g.role}</span></div>
							</div>
							<div className="sc-lane">
								{[0, 1, 2, 3, 4, 5].map((i) => <i className="sc-hline" key={i} style={{ top: i * SC_H }}></i>)}
								{g.offBefore ? <div className="sc-off" style={{ top: 0, height: scY(g.offBefore) }}><span>Off until {scT(g.offBefore)}</span></div> : null}
								{g.offAfter ? <div className="sc-off" style={{ top: scY(g.offAfter), bottom: 0 }}><span>Done at {scT(g.offAfter)}</span></div> : null}
								{g.appts.map((a) => {
									const c = SC_CATS[a.cat]
									return (
										<div key={a.pet}
											className={'sc-appt' + (a.done ? ' done' : '') + (a.now ? ' now' : '') + (a.mins <= 20 ? ' tiny' : '') + (a.drop ? (phase === 'drop' ? ' dropped' : ' predrop') : '')}
											style={{ top: scY(a.at), height: a.mins / 60 * SC_H - 4, '--soft': c[0], '--ac': c[1], '--deep': c[2] }}>
											<div className="sc-appt-top"><strong>{a.pet}{a.drop ? <em className="sc-walkin">Walk-in</em> : null}</strong><span>{scT(a.at)}–{scT(a.at + a.mins)}</span></div>
											{a.mins > 20 ? <span className="sc-appt-svc">{a.breed} · {a.svc}</span> : null}
											{a.done ? <span className="sc-tick"><NIc name="tick" size={10} sw={2.4} /></span> : null}
										</div>
									)
								})}
							</div>
						</div>
					))}
					<div className="sc-nowline" style={{ top: scY(647) + 40 }}><i></i><span>10:47 AM</span></div>
				</div>
			</div>
		</div>
	)
}
